import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import imageCompression from 'browser-image-compression';
import API_BASE_URL from "../../config";

const GymFeedManager = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    image: null,
    preview: null,
  });
  const [errors, setErrors] = useState({ title: "", description: "", image: "" });
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [storageUsed, setStorageUsed] = useState(0); // Baseline storage usage in MB
  const fileInputRef = useRef();
  const userInfo = JSON.parse(localStorage.getItem("fitscan_user")) || {};
  const gymCode = userInfo.gymcode;

  // Load all posts once
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/gym/posts?gymCode=${encodeURIComponent(gymCode)}`,
          {
            method: "GET",
            headers: {
              'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
            }
          }

        );
        const data = await response.json();
        const postsList = data.posts || [];
        // Map posts and set sizeMB based on sizeBytes from the server, or fallback to 0.25 MB
        const mappedPosts = postsList.map(post => ({
          ...post,
          sizeMB: post.sizeBytes ? (post.sizeBytes / (1024 * 1024)) : 0.25
        }));
        setPosts(mappedPosts);

        // Sum sizes of all posts
        const totalSize = mappedPosts.reduce((acc, p) => acc + p.sizeMB, 0);
        setStorageUsed(parseFloat(totalSize.toFixed(2)));
      } catch (err) {
        setPosts([]);
      }
    };
    if (gymCode) {
      fetchPosts();
    }
  }, [gymCode]);

  // Local image preview
  function onImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      image: file,
      preview: URL.createObjectURL(file),
    }));
    setErrors((err) => ({ ...err, image: "" }));
  }

  // Upload post
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors
    const newErrors = { title: "", description: "", image: "" };
    let hasError = false;

    if (!form.title.trim()) {
      newErrors.title = "Please add a Title.";
      hasError = true;
    }
    if (!form.description.trim()) {
      newErrors.description = "Please add a Description.";
      hasError = true;
    }
    if (!form.image) {
      newErrors.image = "Please add a Photo.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    try {
      setUploading(true);

      // Client-side image compression
      const options = {
        maxSizeMB: 1, // Max size 1MB
        maxWidthOrHeight: 1080,
        useWebWorker: true,
        initialQuality: 0.7, // 70% quality
      };
      const compressedImage = await imageCompression(form.image, options);

      // Update mock storage indicator slightly to simulate upload
      const addedSizeMB = compressedImage.size / (1024 * 1024);

      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("gymCode", gymCode);
      fd.append("image", compressedImage);

      const res = await fetch(`${API_BASE_URL}/api/gym/Addposts`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const post = await res.json();
      setPosts((prev) => [post, ...prev]);
      setStorageUsed((prev) => parseFloat((prev + addedSizeMB).toFixed(2))); // Update storage
      setForm({ title: "", description: "", image: null, preview: null });
      setErrors({ title: "", description: "", image: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      alert("Could not upload post. Try again.");
    }
    finally {
      setUploading(false);
    }
  };

  // Delete post
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      setDeletingId(id);
      await fetch(`${API_BASE_URL}/api/gym/posts/${id}`, {
        method: "DELETE"
        , headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch {
      alert("Failed to delete. Try again.");
    }
    setDeletingId(null);
  };

  return (
    <div className="section-padding">
      <h1 className="text-2xl font-bold mb-6 text-lime-400">📷 Gym Social Feed</h1>
      {/* Upload Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-6 max-w-xl mx-auto mb-8 flex flex-col gap-5"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex flex-col gap-2">
            {/* Storage Indicator */}
            <div className="w-32 mb-1">
              <div className="text-xs text-gray-400 mb-1">
                Storage Usage: {storageUsed} MB / 40 MB
              </div>
              <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#6CD800] rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((storageUsed / 40) * 100, 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center justify-center w-32 h-32 border-2 border-dashed border-[#2b2b2b] rounded-lg bg-[#101010] overflow-hidden relative">
              {(form.preview && (
                <img
                  src={form.preview}
                  alt="Preview"
                  className="object-cover w-full h-full aspect-square"
                  style={{ aspectRatio: "1/1" }}
                />
              )) || (
                  <span className="text-gray-400 flex flex-col items-center">
                    <Plus size={32} />
                    <span className="text-xs mt-2 text-center">Add Photo</span>
                  </span>
                )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={onImageChange}
                className="absolute opacity-0 inset-0 cursor-pointer"
              />
            </div>
            {errors.image && (
              <span className="text-red-500 text-[11px] px-1 mt-1 block">{errors.image}</span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <input
              type="text"
              placeholder="Title"
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={50}
            />
            <textarea
              placeholder="Description"
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-lime-400"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={140}
              rows={3}
            />
            <button
              type="submit"
              disabled={uploading}
              className={`mt-2 bg-lime-400 hover:bg-lime-300 disabled:bg-lime-500 disabled:cursor-not-allowed text-[#181f10] font-medium rounded-lg py-2 px-6 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${uploading ? "animate-pulse" : ""
                }`}
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                  Uploading...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Post
                </>
              )}
            </button>

          </div>
        </div>
      </form>

      {/* Feed Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {posts.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-8 bg-[#181818] rounded-lg border border-[#232323]">
            No posts yet.
          </div>
        )}
        {posts.map((post) => (
          <div
            key={post.id}
            className="relative aspect-square bg-[#111] border border-[#232323] rounded-lg flex flex-col overflow-hidden group shadow-md"
          >
            <img
              src={post.imageUrl}
              alt={post.title}
              className="object-cover w-full h-full"
              style={{ aspectRatio: "1/1" }}
            />
            <div className="absolute top-0 left-0 right-0 px-3 pt-2 bg-gradient-to-b from-black/80 to-transparent rounded-t-lg flex items-center gap-2">
              <span className="font-medium truncate text-white" title={post.title}>
                {post.title}
              </span>
            </div>
            {post.description && (
              <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-black/70 text-gray-200 text-xs">
                {post.description}
              </div>
            )}
            <button
              onClick={() => handleDelete(post.id)}
              className={`absolute top-2 right-2 bg-[#232323] text-red-400 rounded-full p-1 hover:bg-red-600 hover:text-white transition
              shadow-lg shadow-black/10 z-10 ${deletingId === post.id ? "opacity-60 pointer-events-none" : ""
                }`}
              title="Delete"
              disabled={deletingId === post.id}
            >
              {deletingId === post.id ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Trash2 size={18} />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GymFeedManager;
